import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme } from '../theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or default to 'light'
  // But since we want to support 'system', we need to store the preference
  const [themePreference, setThemePreference] = useState(() => {
    return localStorage.getItem('themePreference') || 'light';
  });

  // Derived mode for MUI (light or dark)
  const [mode, setMode] = useState('light');

  // Effect to handle system theme changes and preference updates
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const applyTheme = () => {
      let activeMode = 'light';
      
      if (themePreference === 'system') {
        activeMode = mediaQuery.matches ? 'dark' : 'light';
      } else {
        activeMode = themePreference;
      }
      
      setMode(activeMode);
      
      // Update DOM for Tailwind dark mode
      if (activeMode === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    applyTheme();
    localStorage.setItem('themePreference', themePreference);

    const handleChange = () => {
      if (themePreference === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  const value = useMemo(
    () => ({
      theme: themePreference,
      setTheme: (newTheme) => setThemePreference(newTheme),
      mode // exposed for debugging or specific logic
    }),
    [themePreference, mode]
  );

  const muiTheme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
